import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider, useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback } from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ToolPage from "@/pages/ToolPage";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Owner from "@/pages/Owner";
import { GuidePage, GuidesIndex } from "@/pages/Guides";
import ToolsHub from "@/pages/ToolsHub";
import NotFound from "@/pages/NotFound";
import RenewPage, { RenewalAlert } from "@/components/RenewalAlert";
import PAGES from "@/seo/pages.json";
import { TOOLS } from "@/lib/tools";

// Old app links (/precos, /ferramentas/:slug) redirect to the localized SEO URLs
const LegacyPricing = () => {
  const { href } = useLang();
  const { search } = useLocation();
  return <Navigate to={href("pricing") + search} replace />;
};
const LegacyTool = () => {
  const { slug } = useParams();
  const { toolHref, href } = useLang();
  const { search } = useLocation();
  return <Navigate to={(TOOLS[slug] ? toolHref(slug) : href("home")) + search} replace />;
};

const localized = (key, element) => Object.values(PAGES[key].paths).map((p) => <Route key={p} path={p} element={element} />);

function AppRouter() {
  const location = useLocation();
  useEffect(() => { if (!window.location.hash) window.scrollTo(0, 0); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Navbar />
      <main className="pt-[148px]">
        <RenewalAlert />
        <Routes>
          {localized("home", <Landing />)}
          {localized("pricing", <Pricing />)}
          {localized("tools", <ToolsHub />)}
          {Object.entries(PAGES).filter(([, p]) => p.tool && !p.type).flatMap(([k, p]) => localized(k, <ToolPage key={k} slug={p.tool} pageKey={k} />))}
          {localized("guides", <GuidesIndex />)}
          {Object.entries(PAGES).filter(([, p]) => p.type === "guide").flatMap(([k]) => localized(k, <GuidePage key={k} pageKey={k} />))}
          <Route path="/entrar" element={<AuthPage />} />
          <Route path="/painel" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dono" element={<ProtectedRoute><Owner /></ProtectedRoute>} />
          <Route path="/pagamento/sucesso" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/renovar" element={<ProtectedRoute><RenewPage /></ProtectedRoute>} />
          <Route path="/precos" element={<LegacyPricing />} />
          <Route path="/ferramentas/:slug" element={<LegacyTool />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="bottom-right" richColors />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
